import type {
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client'
import { useEffect } from 'react'
import { AppState, AppStateStatus, Platform } from 'react-native'
import Constants from 'expo-constants'
import { File, Paths } from 'expo-file-system'
import AsyncStorage from '@react-native-async-storage/async-storage'
import NetInfo from '@react-native-community/netinfo'
import {
  focusManager,
  MutationCache,
  onlineManager,
  QueryCache,
  QueryClient,
} from '@tanstack/react-query'

import { captureException } from '../utils/sentry'
import { getScopedStorageKey } from '../utils/storage-scope'
import {
  isStorageQuotaExceededError,
  prunePersistedClient,
  shouldPersistQueryKey,
} from './query-persistence'

const QUERY_CACHE_KEY = 'VOCALISE_QUERY_CACHE'
const QUERY_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 7
const isWeb = Platform.OS === 'web'

/**
 * Deterministic cache version derived from build metadata and Contentful environment.
 * Invalidates the persisted cache on new deploys or env switches (develop/master),
 * but not on individual Contentful content publishes.
 */
function getCacheBuster(): string {
  const build = Constants.expoConfig?.extra?.build as
    { version?: string; buildNumber?: string } | undefined
  const contentful = Constants.expoConfig?.extra?.contentful as
    { environment?: string } | undefined

  return [
    build?.version ?? '0',
    build?.buildNumber ?? '0',
    contentful?.environment ?? 'unknown',
  ].join(':')
}

onlineManager.setEventListener((setOnline) => {
  return NetInfo.addEventListener((state) => {
    setOnline(state.isConnected === true)
  })
})

function onAppStateChange(status: AppStateStatus) {
  if (!isWeb) {
    focusManager.setFocused(status === 'active')
  }
}

export function useFocusManager() {
  useEffect(() => {
    if (isWeb) return

    const subscription = AppState.addEventListener('change', onAppStateChange)
    return () => subscription.remove()
  }, [])
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError(error, query) {
      captureException(error, {
        feature: 'query',
        tags: { queryKey: JSON.stringify(query.queryKey).slice(0, 100) },
      })
    },
  }),
  mutationCache: new MutationCache({
    onError(error, _variables, _context, mutation) {
      captureException(error, {
        feature: 'mutation',
        tags: {
          mutationKey: mutation.options.mutationKey
            ? JSON.stringify(mutation.options.mutationKey).slice(0, 100)
            : 'unknown',
        },
      })
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 1000 * 60 * 60 * 24,
      retry: 2,
      refetchOnWindowFocus: !isWeb,
      refetchOnReconnect: true,
    },
  },
})

function getCacheFile(scopedKey: string): File {
  const safeKey = scopedKey.split('::').join('_')
  return new File(Paths.document, `${safeKey}.json`)
}

function createScopedQueryPersister(baseKey: string): Persister {
  // AsyncStorage uses SQLite on Android with a ~2MB row read limit.
  // Large caches (Contentful content queries) exceed this, so we use the
  // file system on native where there is no such constraint.
  const useFileSystem = !isWeb

  return {
    persistClient: async (persistedClient: PersistedClient) => {
      const scopedKey = await getScopedStorageKey(baseKey)
      if (scopedKey == null) {
        return
      }

      const safePersistedClient = prunePersistedClient(persistedClient)
      const serialized = JSON.stringify(safePersistedClient)

      try {
        if (useFileSystem) {
          getCacheFile(scopedKey).write(serialized)
        } else {
          await AsyncStorage.setItem(scopedKey, serialized)
        }
      } catch (error) {
        if (isStorageQuotaExceededError(error)) {
          if (useFileSystem) {
            const file = getCacheFile(scopedKey)
            if (file.exists) file.delete()
          } else {
            await AsyncStorage.removeItem(scopedKey)
          }
          return
        }

        throw error
      }
    },
    restoreClient: async () => {
      const scopedKey = await getScopedStorageKey(baseKey)
      if (scopedKey == null) {
        return undefined
      }

      try {
        if (useFileSystem) {
          const file = getCacheFile(scopedKey)
          if (!file.exists) return undefined
          const serialized = await file.text()
          return JSON.parse(serialized) as PersistedClient
        }

        const serialized = await AsyncStorage.getItem(scopedKey)
        if (serialized == null) return undefined
        return JSON.parse(serialized) as PersistedClient
      } catch (e) {
        captureException(e, {
          feature: 'query',
          tags: { flow: 'restore_cache' },
        })
        return undefined
      }
    },
    removeClient: async () => {
      const scopedKey = await getScopedStorageKey(baseKey)
      if (scopedKey == null) {
        return
      }

      if (useFileSystem) {
        const file = getCacheFile(scopedKey)
        if (file.exists) file.delete()
      } else {
        await AsyncStorage.removeItem(scopedKey)
      }
    },
  }
}

const queryPersister = createScopedQueryPersister(QUERY_CACHE_KEY)

export const queryPersistOptions = {
  persister: queryPersister,
  maxAge: QUERY_CACHE_MAX_AGE_MS,
  buster: getCacheBuster(),
  dehydrateOptions: {
    shouldDehydrateQuery: (query: {
      state: { status: string }
      queryKey: readonly unknown[]
    }) =>
      query.state.status === 'success' && shouldPersistQueryKey(query.queryKey),
  },
}
